/**
 * Local reminder scheduler.
 * Reminders fire while the app/tab is open (or backgrounded).
 * Uses Notifications API + a registered service worker.
 */
import { useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ROUTE_BY_TYPE = {
  meal: "/meal-plan",
  glucose: "/tracking",
  bp: "/tracking",
  weight: "/tracking",
  medication: "/profile",
  water: "/tracking",
};

const TITLE_BY_TYPE = {
  meal: "🍽️ Meal reminder",
  glucose: "🩸 Glucose check",
  bp: "💓 Blood pressure check",
  weight: "⚖️ Weight check-in",
  medication: "💊 Medication",
  water: "💧 Hydration",
};

function nextFireDelay(reminder, now = new Date()) {
  if (!reminder.enabled) return null;
  const [hh, mm] = reminder.time.split(":").map((s) => parseInt(s, 10));
  const days = reminder.days && reminder.days.length ? reminder.days : [0, 1, 2, 3, 4, 5, 6];
  for (let offset = 0; offset < 8; offset++) {
    const cand = new Date(now);
    cand.setDate(now.getDate() + offset);
    cand.setHours(hh, mm, 0, 0);
    if (cand > now && days.includes(cand.getDay())) {
      return cand - now;
    }
  }
  return null;
}

async function fire(reminder) {
  const title = TITLE_BY_TYPE[reminder.type] || "NutriGuide";
  const body = reminder.label + (reminder.note ? ` — ${reminder.note}` : "");
  const url = ROUTE_BY_TYPE[reminder.type] || "/dashboard";
  try {
    const reg = await navigator.serviceWorker.ready;
    if (reg.active) {
      reg.active.postMessage({
        type: "SHOW_NOTIFICATION",
        title, body, url,
        tag: `nutriguide-${reminder.id}-${Date.now()}`,
      });
      return;
    }
  } catch (_) {
    /* fall through */
  }
  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    new Notification(title, { body });
  }
}

export function useReminderScheduler() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;
    if (!("Notification" in window)) return;

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }

    let timers = [];
    let cancelled = false;

    const schedule = async () => {
      try {
        const r = await api.get("/reminders");
        // Clear old
        timers.forEach((t) => clearTimeout(t));
        timers = [];
        const now = new Date();
        for (const rem of r.data) {
          const delay = nextFireDelay(rem, now);
          if (delay == null) continue;
          // Cap at 24h to allow reschedule (also bounds setTimeout precision drift)
          const capped = Math.min(delay, 24 * 60 * 60 * 1000);
          const t = setTimeout(async () => {
            if (cancelled) return;
            if (Notification.permission === "granted") await fire(rem);
            // Reschedule entire list (next day for this reminder, plus others)
            schedule();
          }, capped);
          timers.push(t);
        }
      } catch (_) {
        /* offline or unauth — ignore */
      }
    };

    schedule();

    // Reschedule when reminders change in another tab / on focus
    const onFocus = () => schedule();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      timers.forEach((t) => clearTimeout(t));
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);
}

export async function requestNotificationPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  const r = await Notification.requestPermission();
  return r;
}
